<script setup lang="ts">
import { computed, watch } from 'vue';

import { useTimeUnix } from '@/composables/useTimeUnix';
import { placeholderBackgroundColor } from '@/helpers/colors';
import { formatTransactionTotalAmount } from '@/helpers/formatters';
import { getFallbackText, getLoaderColor, getTitle } from '@/helpers/transactions';
import { type TransactionOTC, TransactionStatusOTC } from '@/references/axios/transactions/types';

import TransactionListItemBase from '@/components/transaction-list-item/TransactionListItemBase.vue';

interface Props {
  tx: TransactionOTC;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: 'expire'): void;
}>();

const amount = computed((): string => {
  return formatTransactionTotalAmount(props.tx.totalAmount, true);
});

const title = computed((): string => {
  return getTitle(props.tx);
});

const fallbackText = computed((): string => getFallbackText(props.tx));
const fallbackColor = computed((): string => placeholderBackgroundColor(fallbackText.value));
const loaderColor = computed((): string | undefined => getLoaderColor(props.tx));

const { tsNow } = useTimeUnix();

const quoteLifetime = computed((): number => {
  if (props.tx.status !== TransactionStatusOTC.Pending) {
    return 1;
  }

  return Math.max(
    (props.tx.quote?.expiredTs ?? props.tx.clearedAt) -
      (props.tx.quote?.createdTs ?? props.tx.createdAt),
    1
  );
});

const quoteSecondsLeft = computed((): number => {
  if (props.tx.status !== TransactionStatusOTC.Pending) {
    return 0;
  }

  return Math.max((props.tx.quote?.expiredTs ?? props.tx.clearedAt) - tsNow.value, 0);
});

const progress = computed((): number | undefined => {
  if (props.tx.status !== TransactionStatusOTC.Pending) {
    return undefined;
  }

  return (quoteSecondsLeft.value / quoteLifetime.value) * 100;
});

watch(progress, (value, oldValue) => {
  if (oldValue && !value) {
    emit('expire');
  }
});
</script>

<template>
  <TransactionListItemBase
    :amount="amount"
    :title="title"
    :icon-fallback-text="fallbackText"
    :icon-src="props.tx.iconUrl"
    :highlighted="props.tx.flags.incoming"
    :strikethrough="props.tx.flags.strikethrough"
    :muted="props.tx.flags.muted"
    :loading="props.tx.flags.pending"
    :skeleton="props.tx.totalAmount.skeleton"
    :failed="props.tx.flags.failed"
    :bordered="props.tx.flags.bordered"
    :loader-color="loaderColor"
    :icon-fallback-color="fallbackColor"
    :not-available="props.tx.totalAmount.notAvailable"
    :progress="progress"
  />
</template>
